import { useState } from "react";
import { Map, GoogleApiWrapper } from "google-maps-react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { mapAction } from "redux/slices/map/mapSlice";
import { modalActions } from "redux/slices/modal";
import { Box, Typography, CircularProgress } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Button from "components/atoms/Button";
import { useTranslation } from "react-i18next";
import yell_Pin from "assets/pin.svg";
import plus from "assets/plus.svg";
import minus from "assets/minus.svg";
import mark from "assets/markblack.svg";

const API_KEY = "AIzaSyDnO5gdUDI1we0x9noUejLSHlM4FKGPr9o";

const GoogleMapComponent = (props: { google: any }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("translation");
  const form = "AddBookingForm";

  const show = useAppSelector((state) => state.map.show);
  const picklatlong = useAppSelector(
    (state) => state.form?.[form]?.values?.pickup?.details?.geometry?.location,
  );
  const destinationlatlong = useAppSelector(
    (state) =>
      state.form?.[form]?.values?.destination?.details?.geometry?.location,
  );
  const pickupAddress = useAppSelector(
    (state) => state.form?.[form]?.values?.pickup?.value,
  );
  const destinationAddress = useAppSelector(
    (state) => state.form?.[form]?.values?.destination?.value,
  );

  const isPickup = show === 0;

  const initialLat = isPickup
    ? (picklatlong?.lat ?? 24.7136)
    : (destinationlatlong?.lat ?? 24.74665);
  const initialLng = isPickup
    ? (picklatlong?.lng ?? 46.6753)
    : (destinationlatlong?.lng ?? 46.65813);

  const [mapRef, setMapRef] = useState<any>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false); // ← tracks if user ever dragged
  const [currentAddress, setCurrentAddress] = useState(
    isPickup ? pickupAddress || "" : destinationAddress || "",
  );
  const [mainCurrentAddress, setMainCurrentAddress] = useState(
    isPickup ? pickupAddress || "" : destinationAddress || "",
  );

  const reverseGeocode = async (lat: number, lng: number) => {
    setIsGeocoding(true);
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${API_KEY}`,
      );
      const data = await response.json();
      if (data.results?.[0]) {
        setCurrentAddress(data.results[0].formatted_address);
        setMainCurrentAddress(data.results?.[7]?.formatted_address);
        if (isPickup) {
          dispatch(mapAction.setStartFormatted(data.results[0]));
        } else {
          dispatch(mapAction.setEndFormatted(data.results[0]));
        }
      }
    } finally {
      setIsGeocoding(false);
    }
  };

  const onMapReady = (_props: any, map: any) => {
    if (!mapRef) setMapRef(map);
  };
  const onMapDragStart = () => {
    setIsDragging(true);
    setHasDragged(true); // ← mark that user has interacted
  };

  const onMapDragEnd = (_props: any, map: any) => {
    setIsDragging(false);
    const center = map.getCenter();
    reverseGeocode(center.lat(), center.lng());
  };

  const handleZoomIn = () => {
    if (mapRef) mapRef.setZoom(mapRef.getZoom() + 1);
  };

  const handleZoomOut = () => {
    if (mapRef) mapRef.setZoom(mapRef.getZoom() - 1);
  };

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }
    if (navigator.permissions) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (result.state === "denied") {
          alert(
            "Location access is blocked. Please enable it in your browser settings.",
          );
          return;
        }
        requestLocation();
      });
    } else {
      requestLocation();
    }
  };

  const requestLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (mapRef) {
          mapRef.panTo({ lat: latitude, lng: longitude });
          setHasDragged(true);
          reverseGeocode(latitude, longitude);
        }
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          alert(
            "Location access was denied. Please enable it in your browser settings.",
          );
        } else {
          alert("Unable to retrieve your location. Please try again.");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  const label = isPickup ? t("pickUp") : t("destination");

  // Address is "confirmed" (fetched) when user dragged and geocoding is done
  const addressReady = hasDragged && currentAddress;

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            fontWeight={700}
            sx={{ fontSize: "0.9rem", color: "#1a1f36", lineHeight: 1.25 }}
          >
            {label}
          </Typography>
        </Box>
      </Box>

      {/* ── Map with fixed center pin ── */}
      <Box
        sx={{
          position: "relative",
          height: 420,
          mx: -4,
          borderTop: "1.5px solid #eaecf4",
          borderBottom: "1.5px solid #eaecf4",
          overflow: "hidden",
        }}
      >
        {/* ── Zoom Controls (top-right) ── */}
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 16,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            zIndex: 12,
          }}
        >
          <Box
            component="button"
            onClick={handleZoomIn}
            sx={{
              width: 40,
              height: 40,
              background: "white",
              border: "none",
              borderRadius: "22px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              transition: "all 0.2s ease",
              "&:hover": { background: "#f5f5f5", transform: "scale(1.05)" },
              "&:active": { transform: "scale(0.95)" },
            }}
          >
            <img src={plus} alt="zoom in" style={{ width: 16, height: 16 }} />
          </Box>
          <Box
            component="button"
            onClick={handleZoomOut}
            sx={{
              width: 40,
              height: 40,
              background: "white",
              border: "none",
              borderRadius: "22px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              transition: "all 0.2s ease",
              "&:hover": { background: "#f5f5f5", transform: "scale(1.05)" },
              "&:active": { transform: "scale(0.95)" },
            }}
          >
            <img src={minus} alt="zoom out" style={{ width: 16, height: 16 }} />
          </Box>
        </Box>

        {/* ── Current Location Button (bottom-right) ── */}
        <Box
          component="button"
          onClick={handleCurrentLocation}
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
            width: 40,
            height: 40,
            background: "white",
            border: "none",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            zIndex: 12,
            transition: "all 0.2s ease",
            "&:hover": { background: "#f5f5f5", transform: "scale(1.05)" },
            "&:active": { transform: "scale(0.95)" },
          }}
        >
          <img
            src={mark}
            alt="current location"
            style={{ width: 20, height: 20 }}
          />
        </Box>

        {/* Shadow dot at pin base */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, 2px)",
            zIndex: 11,
            pointerEvents: "none",
            width: isDragging ? 16 : 10,
            height: isDragging ? 6 : 6,
            borderRadius: "50%",
            bgcolor: "rgba(0,0,0,0.2)",
            filter: "blur(2px)",
            transition: "all 0.15s ease",
          }}
        />

        {/* Center pin */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            zIndex: 12,
            pointerEvents: "none",
            transform: isDragging
              ? "translate(-50%, -115%) scale(1.18)"
              : "translate(-50%, -100%)",
            transition: "transform 0.18s cubic-bezier(0.34,1.56,0.64,1)",
          }}
        >
          <img src={yell_Pin} alt="yelo pin" />
        </Box>
        <Map
          google={props.google}
          // @ts-ignore
          zoom={15}
          initialCenter={{ lat: initialLat, lng: initialLng }}
          style={{ width: "100%", height: "100%" }}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          // @ts-ignore
          disableDefaultUI={true}
          onReady={onMapReady}
          // @ts-ignore
          onIdle={onMapReady}
          // @ts-ignore
          onDragstart={onMapDragStart}
          // @ts-ignore
          onDragend={onMapDragEnd}
          // @ts-ignore
          styles={[
            {
              featureType: "all",
              elementType: "geometry",
              stylers: [{ color: "#E8EAF0" }],
            },
            {
              featureType: "road.highway",
              elementType: "geometry",
              stylers: [{ color: "#7B9BC7" }, { weight: 3 }],
            },
            {
              featureType: "road.highway",
              elementType: "geometry.stroke",
              stylers: [{ color: "#6B8BB7" }, { weight: 1 }],
            },
            {
              featureType: "road.arterial",
              elementType: "geometry",
              stylers: [{ color: "#A8B8D8" }, { weight: 2 }],
            },
            {
              featureType: "road.local",
              elementType: "geometry",
              stylers: [{ color: "#C5D1E8" }, { weight: 1 }],
            },
            {
              featureType: "water",
              elementType: "geometry",
              stylers: [{ color: "#B8CAE8" }],
            },
            {
              featureType: "landscape.natural",
              elementType: "geometry",
              stylers: [{ color: "#DDE2ED" }],
            },
            {
              featureType: "poi.business",
              elementType: "geometry",
              stylers: [{ color: "#D5DAE5" }],
            },
            {
              featureType: "all",
              elementType: "labels.text.fill",
              stylers: [{ color: "#5A6B7A" }],
            },
            {
              featureType: "all",
              elementType: "labels.text.stroke",
              stylers: [{ color: "#FFFFFF" }, { weight: 2 }],
            },
            {
              featureType: "administrative",
              elementType: "labels.text.fill",
              stylers: [{ color: "#4A5A6A" }],
            },
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [{ visibility: "simplified" }],
            },
            {
              featureType: "transit",
              elementType: "labels",
              stylers: [{ visibility: "simplified" }],
            },
            {
              featureType: "road",
              elementType: "labels.text.fill",
              stylers: [{ color: "#4A5A6A" }],
            },
            {
              featureType: "landscape",
              elementType: "geometry",
              stylers: [{ color: "#E8EAF0" }],
            },
            {
              featureType: "transit.station",
              elementType: "geometry",
              stylers: [{ color: "#C8D4E8" }],
            },
          ]}
        />
      </Box>

      {/* ── Footer ── */}
      <Box
        sx={{
          mt: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        {!addressReady ? (
          <Typography
            variant="caption"
            sx={{ color: "#94a3b8", fontSize: "0.75rem" }}
          >
            {isGeocoding ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <CircularProgress size={11} sx={{ color: "#520A76" }} />
                <span>{t("detectingLocation")}</span>
              </Box>
            ) : (
              t("dragMapToAdjustLocation")
            )}
          </Typography>
        ) : (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                {isGeocoding && (
                  <CircularProgress
                    size={11}
                    sx={{ color: "#520A76", flexShrink: 0 }}
                  />
                )}
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    color: "#1a1f36",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {isDragging
                    ? t("moveMapToPin")
                    : isGeocoding
                      ? t("detectingLocation")
                      : mainCurrentAddress || "—"}
                </Typography>
              </Box>
              {!isGeocoding && mainCurrentAddress && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748b",
                    fontSize: "0.76rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                  }}
                >
                  {currentAddress}
                </Typography>
              )}
            </Box>

            <Button
              variant="contained"
              disabled={isGeocoding} // ← also disable while re-fetching
              onClick={() => dispatch(modalActions.closeModal())}
              sx={{
                py: 1,
                px: 3,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.875rem",
                background: "linear-gradient(135deg, #520A76 0%, #7B1FA2 100%)",
                boxShadow: "0 4px 14px rgba(82,10,118,0.25)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #3d0859 0%, #520A76 100%)",
                  boxShadow: "0 6px 20px rgba(82,10,118,0.35)",
                },
                "&.Mui-disabled": {
                  background:
                    "linear-gradient(135deg, #520A76 0%, #7B1FA2 100%)",
                  opacity: 0.5,
                  color: "white",
                },
                flexShrink: 0,
                minWidth: 130,
              }}
            >
              {t("confirm") || "Confirm Location"}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default GoogleApiWrapper({ apiKey: API_KEY })(GoogleMapComponent);
